import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@client/components/hooks/use-toast';
import {
  selectSessionAdvisor,
  selectHierarchyInvitationMutationStatus,
} from '@client/model/selectors';
import {
  postAdvisorHierarchyInvitationByAdvisorIdRequest,
  fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest,
} from '@client/model/advisorHierarchyModel';

export function useAdvisorHierarchyResend() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { isMutating, error: mutationError } = useSelector(selectHierarchyInvitationMutationStatus);

  const advisorId = sessionAdvisor?.advisorId;

  const prevResendingRef = useRef(false);
  const isResendOperationRef = useRef(false);

  // Handle resend mutation completion
  useEffect(() => {
    const isResending = isMutating && isResendOperationRef.current;

    // Mutation just completed (was resending, now not)
    if (prevResendingRef.current && !isMutating && isResendOperationRef.current) {
      if (mutationError) {
        toast({
          title: 'Error',
          description: 'Failed to resend invitation. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else {
        toast({
          title: 'Success',
          description: 'Invitation resent successfully.',
          className: '-translate-y-5',
        });
      }

      // Reset the resend operation flag
      isResendOperationRef.current = false;
    }

    prevResendingRef.current = isResending;
  }, [isMutating, mutationError, toast]);

  const handleResend = (relationship: AdvisorHierarchyRelationship) => {
    if (!advisorId || !relationship.invitationId) {
      toast({
        title: 'Error',
        description: 'Unable to resend invitation. Please try again.',
        variant: 'destructive',
        className: '-translate-y-5',
      });
      return;
    }

    // Mark this as a resend operation
    isResendOperationRef.current = true;

    // Dispatch resend request with invitationId
    dispatch(
      postAdvisorHierarchyInvitationByAdvisorIdRequest({
        advisorId,
        advisorEmail: relationship.advisorEmail,
        isPreview: false,
        invitationId: relationship.invitationId,
        enabledClientAccess: relationship.enabledClientAccess,
      }),
    );
  };

  return {
    // Mutation state
    isResending: isMutating && isResendOperationRef.current,

    // Actions
    handleResend,
  };
}

export type UseAdvisorHierarchyResendReturn = ReturnType<typeof useAdvisorHierarchyResend>;
